import { useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { usePeopleDataContext } from '../../../contexts/PeopleDataContext';
import { peopleApi } from '../../../services/api';
import { getPersonDetailById } from '../../../data/mockPersonDetailData';
import { PersonDetail } from '../../../types/personDetail';

export const usePersonDetailData = () => {
  const { id } = useParams<{ id: string }>();
  const { loading: listLoading, getPersonById } = usePeopleDataContext();
  
  const [profileData, setProfileData] = useState<PersonDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Check if person exists in cached list
  const cachedPerson = useMemo(() => {
    if (!id || listLoading) return null;
    return getPersonById(id);
  }, [id, getPersonById, listLoading]);
  
  // Fetch detailed profile from API
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`[usePersonDetailData] Fetching profile with family for ID: ${id}`);
        // BFF API will fetch, format, and map both profile and family members
        const profileData = await peopleApi.getIndividualProfile(id, true);
        
        if (isMounted) {
          // Data is already formatted by BFF, cast to PersonDetail
          setProfileData(profileData as unknown as PersonDetail);
          const typedProfile = profileData as any;
          console.log(`[usePersonDetailData] ✓ Profile loaded for ${typedProfile.firstName} ${typedProfile.lastName}`);
        }
      } catch (err) {
        if (isMounted) {
          console.error('[usePersonDetailData] Error fetching profile:', err);
          
          // Only use mock data for authentication errors
          const isAuthError = err && typeof err === 'object' && 'response' in err && 
            (err.response as any)?.status && 
            ((err.response as any).status === 401 || (err.response as any).status === 403);
          
          if (isAuthError) {
            console.log('[usePersonDetailData] Authentication error - using mock data for development');
            const mockDetail = getPersonDetailById(id);
            if (mockDetail) {
              setProfileData(mockDetail);
            } else {
              setError(err instanceof Error ? err : new Error('Authentication failed'));
            }
          } else {
            // For other errors, set error state (will fall back to cached data)
            setError(err instanceof Error ? err : new Error('Failed to fetch profile'));
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [id]);
  
  // Determine the final person detail to return
  const personDetail = useMemo(() => {
    // Prefer profile data from API (or mock if auth failed)
    if (profileData) {
      return profileData;
    }
    
    // Fall back to cached list data (limited info) if API failed
    if (cachedPerson && error) {
      console.log('[usePersonDetailData] Using cached list data (limited) due to API error');
      return {
        id: cachedPerson.id,
        firstName: cachedPerson.firstName,
        lastName: cachedPerson.lastName,
        email: cachedPerson.email,
        phoneNumber: cachedPerson.phoneNumber,
        address: cachedPerson.address,
        avatarUrl: cachedPerson.avatarUrl,
      } as PersonDetail;
    }
    
    return null;
  }, [profileData, cachedPerson, error]);

  return { 
    personDetail,
    loading: loading || listLoading,
    error,
    isFromCache: !profileData && !!cachedPerson,
    isFromApi: !!profileData,
  };
};
